import { ensureProtoProps } from '@rehaje/shared'

import { FatalDecoratorLimitMethodError } from '../errors'

import type {
  AnnotationTypes,
  FunctionAnnotation,
  OriginOptions,
  ReactivePrototype
} from '../types'
import type { TFunction, TString } from '@rehaje/shared'

export const globalOrigins: any[] = []

export function createFunctionAnnotation(
  name: AnnotationTypes | TString,
  originFn: TFunction,
  originBound: TFunction
): FunctionAnnotation {
  const annotation = originFn.bind(undefined) as FunctionAnnotation
  annotation._name = name as AnnotationTypes
  annotation.bound = originBound.bind(undefined)
  annotation._esMethodDecorator = annotation.bound
  annotation._tsDecorator = (
    proto: ReactivePrototype | undefined,
    key,
    descriptor
  ) => {
    if (proto !== undefined) {
      const methodKeys = ensureProtoProps(proto, '__rr_method_keys', [])
      if (!methodKeys.includes(key)) methodKeys.push(key)
    }

    return typeof descriptor?.value === 'function'
      ? {
          configurable: false,
          enumerable: true,
          value: annotation.bound(descriptor.value)
        }
      : {
          configurable: false,
          enumerable: true,
          get: new Function(
            `
            return function () {
              return this.__rr_methods_${key}
            }
            `
          )(),
          set: new Function(
            'bound',
            'error',
            `
            return function (fn) {
              if (typeof fn !== 'function') throw error
              this.__rr_methods_${key} = bound(fn)
            }
            `
          )(annotation.bound, new FatalDecoratorLimitMethodError(name, key))
        }
  }
  return annotation
}

export function createOriginFunctionAnnotation(
  name: AnnotationTypes,
  originFn: TFunction,
  originBound: TFunction
): FunctionAnnotation<OriginOptions> {
  return createFunctionAnnotation(
    name,
    (value: TFunction, options?: OriginOptions) => {
      if (
        options?.origin === undefined ||
        (globalOrigins.length > 0 && globalOrigins.includes(options.origin))
      ) {
        return originFn(value)
      }
      globalOrigins.push(options.origin)
      try {
        return originFn(value)
      } finally {
        globalOrigins.pop()
      }
    },
    (value: TFunction, options?: OriginOptions) => {
      if (options?.origin === undefined) return originBound(value)
      return function (this: any, ...args: any[]) {
        if (
          globalOrigins.length > 0 &&
          globalOrigins.includes(options.origin)
        ) {
          return originFn(() => value.apply(this, args))
        }
        globalOrigins.push(options.origin)
        try {
          return originFn(() => value.apply(this, args))
        } finally {
          globalOrigins.pop()
        }
      }
    }
  )
}
